import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, CartesianGrid } from "recharts";
import type { CampaignData } from "@/hooks/useAttributionData";

const PLATFORM_STYLES: Record<string, { label: string; bg: string; text: string }> = {
  meta_ads: { label: "META", bg: "bg-blue-500/20", text: "text-blue-400" },
  google_ads: { label: "GOOGLE", bg: "bg-green-500/20", text: "text-green-400" },
  meta: { label: "META", bg: "bg-blue-500/20", text: "text-blue-400" },
  google: { label: "GOOGLE", bg: "bg-green-500/20", text: "text-green-400" },
};

function fmt(cents: number): string {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1000) return `$${(d / 1000).toFixed(1)}K`;
  return `$${d.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function fmtExact(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Metric({ label, value, sub, color = "text-foreground" }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function DetailRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/30">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-right">
        <span className="text-sm font-semibold text-foreground">{value}</span>
        {sub && <span className="text-[10px] text-muted-foreground ml-1.5">{sub}</span>}
      </div>
    </div>
  );
}

function CampaignDetail({ campaign: c }: { campaign: CampaignData }) {
  const chartData = c.dailyData.map(d => ({
    date: formatDate(d.date),
    spend: d.spendCents / 100,
    leads: d.conversions,
    clicks: d.clicks,
  }));

  // Use exact values from campaign_totals
  const cpc = c.cpcCents;
  const cpl = c.cplCents;
  const cpm = c.cpmCents;
  const ctrAll = c.ctr;
  const costPerResult = c.costPerResultCents;
  const costPerDemo = c.cpdCents;

  return (
    <div className="border-t border-border/50 pt-4 mt-4 space-y-5" onClick={e => e.stopPropagation()}>
      {/* Daily chart */}
      {chartData.length > 1 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Daily Performance</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <RechartsTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                <Bar yAxisId="left" dataKey="spend" fill="hsl(217, 91%, 60%)" name="Spend ($)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="leads" fill="hsl(142, 71%, 45%)" name="Leads" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Full metrics — two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        {/* Left column: Ad Performance */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Ad Performance</p>
          <DetailRow label="Amount Spent" value={fmtExact(c.spendCents)} />
          <DetailRow label="Reach" value={c.reach > 0 ? c.reach.toLocaleString() : "—"} sub="unique people" />
          <DetailRow label="Frequency" value={c.frequency > 0 ? c.frequency.toFixed(2) : "—"} sub="avg times seen" />
          <DetailRow label="Impressions" value={c.impressions.toLocaleString()} />
          <DetailRow label="Link Clicks" value={c.linkClicks > 0 ? c.linkClicks.toLocaleString() : c.clicks.toLocaleString()} />
          <DetailRow label="Clicks (all)" value={c.clicks.toLocaleString()} />
          <DetailRow label="CTR (all)" value={fmtPct(ctrAll)} />
          <DetailRow label="CPC (all)" value={cpc > 0 ? fmtExact(cpc) : "—"} />
          <DetailRow label="CPM" value={cpm > 0 ? fmtExact(cpm) : "—"} sub="per 1K impressions" />
          <DetailRow label="Results" value={c.conversions.toLocaleString()} sub="website schedules" />
          <DetailRow label="Cost per Result" value={costPerResult > 0 ? fmtExact(costPerResult) : "—"} />
          <DetailRow label="Cost per Lead" value={cpl > 0 ? fmtExact(cpl) : "—"} />
        </div>

        {/* Right column: Pipeline & Revenue */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Pipeline & Revenue</p>
          <DetailRow label="Leads in Pipeline" value={c.leads.toLocaleString()} />
          <DetailRow label="Demos Booked" value={c.demosBooked.toLocaleString()} />
          <DetailRow label="Cost per Demo" value={costPerDemo > 0 ? fmtExact(Math.round(costPerDemo)) : "—"} />
          <DetailRow label="Demos Closed" value={c.demosClosed.toLocaleString()} />
          <DetailRow label="Revenue" value={fmt(c.totalRevenueCents)} />
          <DetailRow label="Purchase ROAS" value={c.roas !== null ? `${c.roas}x` : "—"} sub="return on ad spend" />
          {c.dateRange && (
            <DetailRow label="Campaign Period" value={`${formatDate(c.dateRange.earliest)} — ${formatDate(c.dateRange.latest)}`} sub={`${c.dailyData.length} days`} />
          )}
        </div>
      </div>

      {/* Recent leads table */}
      {c.recentLeads.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Leads</p>
          <table className="w-full text-xs">
            <thead><tr className="text-muted-foreground border-b border-border">
              <th className="text-left py-1.5 font-medium">Contact</th>
              <th className="text-left py-1.5 font-medium">Stage</th>
              <th className="text-left py-1.5 font-medium">Setter</th>
              <th className="text-left py-1.5 font-medium">Closer</th>
              <th className="text-right py-1.5 font-medium">Deal Value</th>
            </tr></thead>
            <tbody>{c.recentLeads.map(lead => (
              <tr key={lead.id} className="border-b border-border/30">
                <td className="py-1.5 text-foreground">{lead.contact_name || "—"}</td>
                <td className="py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">{lead.stage}</span></td>
                <td className="py-1.5 text-muted-foreground">{lead.setter_name ?? "—"}</td>
                <td className="py-1.5 text-muted-foreground">{lead.closer_name ?? "—"}</td>
                <td className="py-1.5 text-right">{lead.deal_value_cents ? <span className="text-emerald-400">{fmt(lead.deal_value_cents)}</span> : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface CampaignsTabProps { campaigns: CampaignData[]; loading: boolean; }

export function CampaignsTab({ campaigns, loading }: CampaignsTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) return <div className="space-y-4">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="rounded-lg border border-border bg-card p-5 h-32 animate-pulse" />)}</div>;

  const visible = campaigns.filter(c => c.spendCents > 0 || c.leads > 0);
  if (visible.length === 0) return <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No campaign data for this period. Try selecting "All" time range.</div>;

  const sorted = [...visible].sort((a, b) => b.spendCents - a.spendCents);

  return (
    <div className="space-y-3">
      {sorted.map(campaign => {
        const isExpanded = expandedId === campaign.campaignId;
        const ps = PLATFORM_STYLES[campaign.platform.toLowerCase()] ?? { label: campaign.platform.toUpperCase(), bg: "bg-muted", text: "text-muted-foreground" };

        // Use exact values from Meta (via campaign_totals)
        const cpc = campaign.cpcCents;
        const cpl = campaign.cplCents;
        const cpm = campaign.cpmCents;
        const ctrAll = campaign.ctr;
        const costPerResult = campaign.costPerResultCents;

        return (
          <div
            key={campaign.campaignId}
            className={`rounded-lg border bg-card transition-all duration-200 ${isExpanded ? "border-primary/40" : "border-border hover:border-primary/20"}`}
          >
            {/* Collapsed card — 6 key metrics */}
            <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : campaign.campaignId)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${ps.bg} ${ps.text}`}>{ps.label}</span>
                  <span className="text-sm font-semibold text-foreground truncate">{campaign.campaignName}</span>
                  {campaign.dateRange && (
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(campaign.dateRange.earliest)} — {formatDate(campaign.dateRange.latest)}</span>
                  )}
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <Metric label="Results" value={campaign.conversions.toLocaleString()} color="text-blue-400" />
                <Metric label="Amount Spent" value={fmt(campaign.spendCents)} color="text-foreground" />
                <Metric label="CPC" value={cpc > 0 ? fmtExact(cpc) : "—"} sub="cost per click" />
                <Metric label="Cost per Result" value={costPerResult > 0 ? fmtExact(costPerResult) : "—"} />
                <Metric label="CPM" value={cpm > 0 ? fmtExact(cpm) : "—"} sub="per 1K impressions" />
                <Metric label="CTR (all)" value={ctrAll > 0 ? fmtPct(ctrAll) : "—"} sub={`${campaign.clicks.toLocaleString()} clicks`} />
              </div>
            </div>

            {isExpanded && (
              <div className="px-4 pb-4">
                <CampaignDetail campaign={campaign} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
